import { Injectable, Renderer2, RendererFactory2, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * Accessibility Service
 * 
 * Provides utilities for managing accessibility features throughout the application.
 * Includes focus management, live announcements, keyboard navigation helpers, and more.
 */
@Injectable({
  providedIn: 'root'
})
export class AccessibilityService {
  private renderer: Renderer2;
  private liveRegion: HTMLElement | null = null;

  constructor(
    private rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
    private router: Router
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.initializeLiveRegion();
    this.setupRouteAnnouncements();
  }

  /**
   * Initialize a live region for screen reader announcements
   */
  private initializeLiveRegion(): void {
    this.liveRegion = this.document.createElement('div');
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('aria-atomic', 'true');
    this.liveRegion.setAttribute('role', 'status');
    this.liveRegion.style.position = 'absolute';
    this.liveRegion.style.left = '-10000px';
    this.liveRegion.style.width = '1px';
    this.liveRegion.style.height = '1px';
    this.liveRegion.style.overflow = 'hidden';
    this.document.body.appendChild(this.liveRegion);
  }

  /**
   * Announce message to screen readers
   * @param message - Message to announce
   * @param priority - 'polite' (default) or 'assertive'
   * @param clearPrevious - Clear previous announcement before new one
   */
  announce(message: string, priority: 'polite' | 'assertive' = 'polite', clearPrevious = true): void {
    if (!this.liveRegion) {
      return;
    }

    this.liveRegion.setAttribute('aria-live', priority);

    if (clearPrevious) {
      this.liveRegion.textContent = '';
    }

    // Small delay to ensure screen readers pick up the change
    setTimeout(() => {
      if (this.liveRegion) {
        this.liveRegion.textContent = message;
      }
    }, 100);

    // Clear after announcement to allow same message to be announced again
    setTimeout(() => {
      if (this.liveRegion && clearPrevious) {
        this.liveRegion.textContent = '';
      }
    }, 1000);
  }

  /**
   * Set up automatic route change announcements
   */
  private setupRouteAnnouncements(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        const title = this.document.title || 'Page';
        this.announce(`Navigated to ${title}`);
        
        // Move focus to main content or page heading
        this.focusMainContent();
      });
  }

  /**
   * Focus on main content area after navigation
   */
  focusMainContent(): void {
    // Try to focus on main element or first h1
    const mainElement = this.document.querySelector('main, [role="main"], h1') as HTMLElement;
    if (mainElement) {
      mainElement.setAttribute('tabindex', '-1');
      mainElement.focus();
      
      // Remove tabindex after focus to maintain natural tab order
      mainElement.addEventListener('blur', () => {
        mainElement.removeAttribute('tabindex');
      }, { once: true });
    }
  }

  /**
   * Set focus to a specific element by selector
   * @param selector - CSS selector for the element
   */
  focusElement(selector: string): void {
    const element = this.document.querySelector(selector) as HTMLElement;
    if (element) {
      element.focus();
    }
  }

  /**
   * Set focus to first focusable element within a container
   * @param container - Container element or selector
   */
  focusFirstElement(container: HTMLElement | string): void {
    const containerEl = typeof container === 'string' 
      ? this.document.querySelector(container) as HTMLElement
      : container;
    
    if (!containerEl) {
      return;
    }

    const focusableElements = this.getFocusableElements(containerEl);
    if (focusableElements.length > 0) {
      (focusableElements[0] as HTMLElement).focus();
    }
  }

  /**
   * Get all focusable elements within a container
   * @param container - Container element
   * @returns Array of focusable elements
   */
  getFocusableElements(container: HTMLElement): HTMLElement[] {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable]'
    ].join(', ');

    return Array.from(container.querySelectorAll(focusableSelectors)) as HTMLElement[];
  }

  /**
   * Trap focus within a container (useful for modals)
   * @param container - Container element
   * @returns Function to remove trap
   */
  trapFocus(container: HTMLElement): () => void {
    const focusableElements = this.getFocusableElements(container);
    
    if (focusableElements.length === 0) {
      return () => {};
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') {
        return;
      }

      if (e.shiftKey) {
        // Shift + Tab
        if (this.document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (this.document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    container.addEventListener('keydown', handleTabKey);

    // Focus first element
    firstElement.focus();

    // Return cleanup function
    return () => {
      container.removeEventListener('keydown', handleTabKey);
    };
  }

  /**
   * Add skip link to bypass navigation
   * Should be called in app initialization
   */
  addSkipLink(): void {
    const skipLink = this.document.createElement('a');
    skipLink.href = '#main-content';
    skipLink.textContent = 'Skip to main content';
    skipLink.className = 'skip-link';
    skipLink.addEventListener('click', (e) => {
      e.preventDefault();
      const mainContent = this.document.getElementById('main-content') || 
                         this.document.querySelector('main') as HTMLElement;
      if (mainContent) {
        mainContent.setAttribute('tabindex', '-1');
        mainContent.focus();
        mainContent.addEventListener('blur', () => {
          mainContent.removeAttribute('tabindex');
        }, { once: true });
      }
    });

    // Insert at the beginning of body
    if (this.document.body.firstChild) {
      this.document.body.insertBefore(skipLink, this.document.body.firstChild);
    } else {
      this.document.body.appendChild(skipLink);
    }
  }

  /**
   * Check if user prefers reduced motion
   */
  prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Set page title (important for screen readers)
   * @param title - Page title
   */
  setPageTitle(title: string): void {
    this.document.title = title;
  }

  /**
   * Generate unique ID for accessibility attributes
   * @param prefix - Prefix for the ID
   */
  generateId(prefix: string = 'a11y'): string {
    return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Check if an element is visible
   * @param element - Element to check
   */
  isElementVisible(element: HTMLElement): boolean {
    return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
  }

  /**
   * Handle keyboard navigation for custom components
   * @param event - Keyboard event
   * @param handlers - Object with key handlers
   */
  handleKeyboardNavigation(
    event: KeyboardEvent,
    handlers: { [key: string]: () => void }
  ): void {
    const handler = handlers[event.key];
    if (handler) {
      event.preventDefault();
      handler();
    }
  }

  /**
   * Add loading announcement
   * @param message - Loading message
   */
  announceLoading(message: string = 'Loading...'): void {
    this.announce(message, 'polite');
  }

  /**
   * Add error announcement
   * @param message - Error message
   */
  announceError(message: string): void {
    this.announce(`Error: ${message}`, 'assertive');
  }

  /**
   * Add success announcement
   * @param message - Success message
   */
  announceSuccess(message: string): void {
    this.announce(message, 'polite');
  }

  /**
   * Check color contrast ratio (simplified)
   * Note: This is a basic implementation. For production, use a library like tinycolor2
   */
  checkColorContrast(foreground: string, background: string): { ratio: number; passes: { aa: boolean; aaa: boolean } } {
    // This is a placeholder - implement full contrast checking with a proper library
    console.warn('Color contrast checking requires additional implementation');
    return {
      ratio: 0,
      passes: { aa: false, aaa: false }
    };
  }

  /**
   * Clean up service resources
   */
  ngOnDestroy(): void {
    if (this.liveRegion && this.liveRegion.parentNode) {
      this.liveRegion.parentNode.removeChild(this.liveRegion);
    }
  }
}
